import { formatExternalApiDateTime } from "@/Utile/ncrmExternalApi.mjs";
import { DATA_ENRICHMENT_TARGET_FIELD_OPTIONS, DEDUP_FIELD_OPTIONS } from "@/Utile/ncrmWorkflowOptions.mjs";
import {
  buildContactNormalizationSummary,
  createDefaultEmailNormalizationSettings,
  createDefaultPhoneNormalizationSettings,
} from "@/Utile/ncrmContactNormalizationWorkflow.mjs";
import {
  createWorkflowNodeDefinitions,
  createWorkflowTemplates,
} from "@/Utile/ncrmWorkflowDefinitions.mjs";
import { createMailjetEngagementNodeDefaults } from "@/Utile/ncrmMailjetEngagementWorkflow.mjs";
export { DATA_ENRICHMENT_TARGET_FIELD_OPTIONS, DEDUP_FIELD_OPTIONS } from "@/Utile/ncrmWorkflowOptions.mjs";
export { createWorkflowNodeDefinitions, createWorkflowTemplates } from "@/Utile/ncrmWorkflowDefinitions.mjs";
export {
  CONTACT_NORMALIZATION_DEFAULT_WORKFLOW_INSTANCE_ID,
  CONTACT_NORMALIZATION_NODE_KEY_TO_TYPE,
  CONTACT_NORMALIZATION_NODE_TYPES,
} from "@/Utile/ncrmContactNormalizationWorkflow.mjs";

export const WORKFLOW_STORAGE_KEY = "ncrm:workflow-instances:v1";
export const DEFAULT_WORKFLOW_INSTANCE_ID = "wf-enrichment-of-data-default";

export const WORKFLOW_TEMPLATE_KEYS = Object.freeze({
  ENRICHMENT_OF_DATA: "enrichment-of-data",
  CONTACT_NORMALIZATION: "contact-normalization",
  MAILJET_ENGAGEMENT: "mailjet-engagement",
});

export const WORKFLOW_NODE_KEYS = Object.freeze({
  EXTERNAL_FORM_WIDGET: "external-form-widget",
  API_IMPORT: "api-import",
  FILE_IMPORT: "file-import",
  DUPLICATE_CHECK: "duplicate-check",
  DATA_ENRICHMENT: "data-enrichment",
  CRM_WRITE: "crm-write",
  LEAD_BOARD_ADD: "lead-board-add",
  PHONE_NORMALIZATION: "phone-normalization",
  EMAIL_NORMALIZATION: "email-normalization",
  MAILJET_EVENT_TRIGGER: "mailjet-event-trigger",
  CONTACT_EXISTENCE_CHECK: "contact-existence-check",
  LEAD_PRESENCE_CHECK: "lead-presence-check",
  AI_COMPATIBILITY_CHECK: "ai-compatibility-check",
  MAILJET_LEAD_ADD: "mailjet-lead-add",
  TELEGRAM_NOTIFY: "telegram-notify",
});

export { PUBLIC_EXTERNAL_FORM_SOURCE_SYSTEMS } from "@/Utile/ncrmExternalFormSources.mjs";

export const WORKFLOW_NODE_ORDER = Object.freeze([
  WORKFLOW_NODE_KEYS.EXTERNAL_FORM_WIDGET,
  WORKFLOW_NODE_KEYS.API_IMPORT,
  WORKFLOW_NODE_KEYS.FILE_IMPORT,
  WORKFLOW_NODE_KEYS.DUPLICATE_CHECK,
  WORKFLOW_NODE_KEYS.DATA_ENRICHMENT,
  WORKFLOW_NODE_KEYS.CRM_WRITE,
  WORKFLOW_NODE_KEYS.LEAD_BOARD_ADD,
]);

/** Best-practice default: 15 min — mirrors HubSpot / Salesforce polling interval */
export const AUTO_SYNC_INTERVAL_OPTIONS = Object.freeze([
  { minutes: 5,    labelKey: "ncrm.workflows.nodeSettings.autoSyncIntervals.5min"  },
  { minutes: 15,   labelKey: "ncrm.workflows.nodeSettings.autoSyncIntervals.15min" },
  { minutes: 30,   labelKey: "ncrm.workflows.nodeSettings.autoSyncIntervals.30min" },
  { minutes: 60,   labelKey: "ncrm.workflows.nodeSettings.autoSyncIntervals.60min" },
  { minutes: 240,  labelKey: "ncrm.workflows.nodeSettings.autoSyncIntervals.240min"},
  { minutes: 1440, labelKey: "ncrm.workflows.nodeSettings.autoSyncIntervals.1440min"},
]);

export const AUTO_SYNC_DEFAULT_INTERVAL_MINUTES = 15;

export const DEDUP_DEFAULT_DUPLICATE_THRESHOLD = 65;
export const DEDUP_DEFAULT_NEW_RECORD_THRESHOLD = 20;

export const DATA_ENRICHMENT_DEFAULT_PROMPT_KEY = "ncrm_company_enrichment";
export const DATA_ENRICHMENT_DEFAULT_PROFILE_KEY = "enrich_research";
export const DATA_ENRICHMENT_DEFAULT_BATCH_LIMIT = 25;

export const DATA_ENRICHMENT_STATUS_SCOPE = Object.freeze([
  "pending_enrichment",
  "enriched_pending_dedup",
  "pending_review",
  "approved",
  "rejected",
  "written",
  "written_new_company",
  "written_existing_company",
]);

export const CRM_WRITE_STATUS_SCOPE = Object.freeze([
  "approved",
  "written",
  "written_new_company",
  "written_existing_company",
]);

export const LEAD_BOARD_STATUS_SCOPE = Object.freeze([
  "written_new_company",
  "lead_board_added",
  "lead_board_already_exists",
  "lead_board_error",
]);

export const DATA_ENRICHMENT_DEFAULT_TARGET_FIELDS = Object.freeze(
  DATA_ENRICHMENT_TARGET_FIELD_OPTIONS.map((option) => option.value)
);

export const DEDUP_METHOD_OPTIONS = Object.freeze([
  { value: "auto", labelKey: "ncrm.workflows.nodeSettings.dedup.methods.auto" },
  { value: "identifier_exact", labelKey: "ncrm.workflows.nodeSettings.dedup.methods.identifierExact" },
  { value: "fuzzy_text", labelKey: "ncrm.workflows.nodeSettings.dedup.methods.fuzzyText" },
  { value: "url_exact", labelKey: "ncrm.workflows.nodeSettings.dedup.methods.urlExact" },
  { value: "phone_8_digits", labelKey: "ncrm.workflows.nodeSettings.dedup.methods.phoneDigits" },
  { value: "email_exact", labelKey: "ncrm.workflows.nodeSettings.dedup.methods.emailExact" },
  { value: "normalized_exact", labelKey: "ncrm.workflows.nodeSettings.dedup.methods.normalizedExact" },
]);

export const createDefaultDedupMatchFields = () => [
  {
    id: "identity",
    label: "Fiscal / VAT / registration",
    stagingField: "registration_ids",
    companyField: "registration_ids",
    method: "identifier_exact",
    weight: 25,
    forceExactScore: true,
  },
  {
    id: "name",
    label: "Name",
    stagingField: "name",
    companyField: "name",
    method: "fuzzy_text",
    weight: 20,
    forceExactScore: false,
  },
  {
    id: "website",
    label: "Website",
    stagingField: "website",
    companyField: "website",
    method: "url_exact",
    weight: 15,
    forceExactScore: false,
  },
  {
    id: "phone",
    label: "Phone",
    stagingField: "phone_all",
    companyField: "phone_all",
    method: "phone_8_digits",
    weight: 15,
    forceExactScore: false,
  },
  {
    id: "email",
    label: "Email",
    stagingField: "email_all",
    companyField: "email_all",
    method: "email_exact",
    weight: 15,
    forceExactScore: false,
  },
  {
    id: "social",
    label: "Social profiles",
    stagingField: "social_profiles",
    companyField: "social_profiles",
    method: "url_exact",
    weight: 10,
    forceExactScore: false,
  },
];

export const createDefaultDuplicateCheckSettings = () => ({
  status: "active",
  description: "",
  duplicateThreshold: DEDUP_DEFAULT_DUPLICATE_THRESHOLD,
  newRecordThreshold: DEDUP_DEFAULT_NEW_RECORD_THRESHOLD,
  autoRun: false,
  autoRunIntervalMinutes: AUTO_SYNC_DEFAULT_INTERVAL_MINUTES,
  skipAiVerification: false,
  matchFields: createDefaultDedupMatchFields(),
});

export const createDefaultDataEnrichmentSettings = () => ({
  status: "active",
  description: "",
  mode: "manual",
  promptKey: DATA_ENRICHMENT_DEFAULT_PROMPT_KEY,
  aiProfileKey: DATA_ENRICHMENT_DEFAULT_PROFILE_KEY,
  targetFields: [...DATA_ENRICHMENT_DEFAULT_TARGET_FIELDS],
  rerunDedupAfterEnrichment: true,
  maxRowsPerRun: DATA_ENRICHMENT_DEFAULT_BATCH_LIMIT,
  autoRunIntervalMinutes: 60,
});

export const createDefaultCrmWriteSettings = () => ({
  status: "active",
  description: "",
  autoRun: false,
  autoRunIntervalMinutes: AUTO_SYNC_DEFAULT_INTERVAL_MINUTES,
});

export const createDefaultLeadBoardAddSettings = () => ({
  status: "inactive",
  description: "",
  autoRun: false,
  autoRunIntervalMinutes: AUTO_SYNC_DEFAULT_INTERVAL_MINUTES,
  boardId: null,
  stageId: null,
});

export const createDefaultExternalFormWidgetSettings = () => ({
  status: "active",
  description: "",
  defaultDelaySeconds: 20,
});

export const getDefaultWorkflowNodeSettings = (endpointId = null) => ({
  [WORKFLOW_NODE_KEYS.EXTERNAL_FORM_WIDGET]: {
    ...createDefaultExternalFormWidgetSettings(),
  },
  [WORKFLOW_NODE_KEYS.API_IMPORT]: {
    endpointId,
    status: "active",
    entityScope: "inherit",
    payloadFormat: "json",
    description: "",
    autoSync: false,
    autoSyncIntervalMinutes: AUTO_SYNC_DEFAULT_INTERVAL_MINUTES,
  },
  [WORKFLOW_NODE_KEYS.FILE_IMPORT]: {
    status: "active",
    description: "",
    autoDedupAfterImport: false,
  },
  [WORKFLOW_NODE_KEYS.DUPLICATE_CHECK]: {
    ...createDefaultDuplicateCheckSettings(),
  },
  [WORKFLOW_NODE_KEYS.DATA_ENRICHMENT]: {
    ...createDefaultDataEnrichmentSettings(),
  },
  [WORKFLOW_NODE_KEYS.CRM_WRITE]: {
    ...createDefaultCrmWriteSettings(),
  },
  [WORKFLOW_NODE_KEYS.LEAD_BOARD_ADD]: {
    ...createDefaultLeadBoardAddSettings(),
  },
  [WORKFLOW_NODE_KEYS.PHONE_NORMALIZATION]: {
    ...createDefaultPhoneNormalizationSettings(),
  },
  [WORKFLOW_NODE_KEYS.EMAIL_NORMALIZATION]: {
    ...createDefaultEmailNormalizationSettings(),
  },
  ...createMailjetEngagementNodeDefaults(),
});

const createId = () => `wf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const createWorkflowInstance = ({
  id = createId(),
  name,
  templateKey = WORKFLOW_TEMPLATE_KEYS.ENRICHMENT_OF_DATA,
  endpointId = null,
} = {}) => ({
  id,
  name: String(name || "").trim(),
  templateKey,
  status: "draft",
  version: "ui-draft",
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  nodeSettings: getDefaultWorkflowNodeSettings(endpointId),
});

export const normalizeWorkflowInstance = (workflow = {}, { fallbackName = "", endpointId = null } = {}) => ({
  ...createWorkflowInstance({
    id: workflow.id || undefined,
    name: workflow.name || fallbackName,
    templateKey: workflow.templateKey || workflow.template_key || WORKFLOW_TEMPLATE_KEYS.ENRICHMENT_OF_DATA,
    endpointId,
  }),
  ...workflow,
  templateKey: workflow.templateKey || workflow.template_key || WORKFLOW_TEMPLATE_KEYS.ENRICHMENT_OF_DATA,
  createdAt: workflow.createdAt || workflow.created_at || workflow.createdAt,
  updatedAt: workflow.updatedAt || workflow.updated_at || workflow.updatedAt,
  nodeSettings: {
    ...getDefaultWorkflowNodeSettings(endpointId),
    ...(workflow.nodeSettings || workflow.node_settings || {}),
    [WORKFLOW_NODE_KEYS.DUPLICATE_CHECK]: {
      ...createDefaultDuplicateCheckSettings(),
      ...((workflow.nodeSettings || workflow.node_settings || {})[WORKFLOW_NODE_KEYS.DUPLICATE_CHECK] || {}),
      matchFields:
        ((workflow.nodeSettings || workflow.node_settings || {})[WORKFLOW_NODE_KEYS.DUPLICATE_CHECK]?.matchFields || [])
          .length
          ? (workflow.nodeSettings || workflow.node_settings || {})[WORKFLOW_NODE_KEYS.DUPLICATE_CHECK].matchFields
          : createDefaultDedupMatchFields(),
    },
    [WORKFLOW_NODE_KEYS.DATA_ENRICHMENT]: {
      ...createDefaultDataEnrichmentSettings(),
      ...((workflow.nodeSettings || workflow.node_settings || {})[WORKFLOW_NODE_KEYS.DATA_ENRICHMENT] || {}),
      targetFields:
        ((workflow.nodeSettings || workflow.node_settings || {})[WORKFLOW_NODE_KEYS.DATA_ENRICHMENT]?.targetFields || [])
          .length
          ? (workflow.nodeSettings || workflow.node_settings || {})[WORKFLOW_NODE_KEYS.DATA_ENRICHMENT].targetFields
          : createDefaultDataEnrichmentSettings().targetFields,
    },
    [WORKFLOW_NODE_KEYS.CRM_WRITE]: {
      ...createDefaultCrmWriteSettings(),
      ...((workflow.nodeSettings || workflow.node_settings || {})[WORKFLOW_NODE_KEYS.CRM_WRITE] || {}),
    },
    [WORKFLOW_NODE_KEYS.LEAD_BOARD_ADD]: {
      ...createDefaultLeadBoardAddSettings(),
      ...((workflow.nodeSettings || workflow.node_settings || {})[WORKFLOW_NODE_KEYS.LEAD_BOARD_ADD] || {}),
    },
    [WORKFLOW_NODE_KEYS.EXTERNAL_FORM_WIDGET]: {
      ...createDefaultExternalFormWidgetSettings(),
      ...((workflow.nodeSettings || workflow.node_settings || {})[WORKFLOW_NODE_KEYS.EXTERNAL_FORM_WIDGET] || {}),
    },
    [WORKFLOW_NODE_KEYS.PHONE_NORMALIZATION]: {
      ...createDefaultPhoneNormalizationSettings(),
      ...((workflow.nodeSettings || workflow.node_settings || {})[WORKFLOW_NODE_KEYS.PHONE_NORMALIZATION] || {}),
    },
    [WORKFLOW_NODE_KEYS.EMAIL_NORMALIZATION]: {
      ...createDefaultEmailNormalizationSettings(),
      ...((workflow.nodeSettings || workflow.node_settings || {})[WORKFLOW_NODE_KEYS.EMAIL_NORMALIZATION] || {}),
    },
  },
});

const normalizeWorkflow = (workflow = {}, fallbackName = "", endpointId = null) =>
  normalizeWorkflowInstance(workflow, { fallbackName, endpointId });

export const loadWorkflowInstances = ({ fallbackName = "", endpointId = null } = {}) => {
  const fallbackWorkflow = () =>
    createWorkflowInstance({
      id: DEFAULT_WORKFLOW_INSTANCE_ID,
      name: fallbackName,
      endpointId,
    });

  if (typeof window === "undefined") {
    return [fallbackWorkflow()];
  }

  try {
    const storedValue = window.localStorage.getItem(WORKFLOW_STORAGE_KEY);
    if (storedValue === null) return [fallbackWorkflow()];
    const parsed = JSON.parse(storedValue || "[]");
    const stored = Array.isArray(parsed) ? parsed : [];
    return stored.map((workflow) => normalizeWorkflow(workflow, fallbackName, endpointId));
  } catch (_) {
    return [fallbackWorkflow()];
  }
};

export const saveWorkflowInstances = (workflows = []) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(WORKFLOW_STORAGE_KEY, JSON.stringify(workflows));
};

export const updateWorkflowInstance = (workflows = [], workflowId, patch = {}) =>
  workflows.map((workflow) =>
    workflow.id === workflowId
      ? {
          ...workflow,
          ...patch,
          updatedAt: new Date().toISOString(),
        }
      : workflow
  );

export const updateWorkflowNodeSettings = (workflows = [], workflowId, nodeKey, settings = {}) =>
  updateWorkflowInstance(workflows, workflowId, {
    nodeSettings: {
      ...(workflows.find((workflow) => workflow.id === workflowId)?.nodeSettings || {}),
      [nodeKey]: {
        ...(workflows.find((workflow) => workflow.id === workflowId)?.nodeSettings?.[nodeKey] || {}),
        ...settings,
      },
    },
  });

export const removeWorkflowInstance = (workflows = [], workflowId) =>
  workflows.filter((workflow) => workflow.id !== workflowId);

export const resolveWorkflowApiEndpointId = (workflow = {}, endpoints = []) => {
  const configured = Number(workflow?.nodeSettings?.[WORKFLOW_NODE_KEYS.API_IMPORT]?.endpointId);
  if (Number.isFinite(configured) && configured > 0) return configured;
  const firstEndpoint = Array.isArray(endpoints) ? endpoints[0] : null;
  return firstEndpoint?.id || null;
};

export const formatWorkflowStatus = (status = "") => String(status || "draft").replace(/_/g, " ");

export const mapExternalApiDeliveryToWorkflowEvent = (delivery = {}, endpoint = {}, locale = "ro-RO") => {
  const status = String(delivery.status || "").trim();
  const hasError = Boolean(delivery.error_code || delivery.error_message);
  return {
    id: delivery.id,
    receivedAt: formatExternalApiDateTime(delivery.received_at || delivery.created_at, locale),
    externalId: delivery.external_id || "-",
    sourceApi: delivery.endpoint_name || endpoint.name || delivery.endpoint_key || endpoint.endpoint_key || "-",
    entityType: delivery.entity_scope || endpoint.entity_scope || "-",
    status: status || "-",
    result: hasError ? "warning" : "stored",
    warning: delivery.error_message || delivery.error_code || "-",
    raw: delivery,
  };
};

export const buildWorkflowSummary = (workflow = {}, endpoints = []) => {
  if (workflow?.templateKey === WORKFLOW_TEMPLATE_KEYS.CONTACT_NORMALIZATION) {
    return buildContactNormalizationSummary();
  }
  const endpointId = resolveWorkflowApiEndpointId(workflow, endpoints);
  const endpoint = (Array.isArray(endpoints) ? endpoints : []).find((item) => item.id === endpointId) || null;
  return {
    endpointId,
    endpoint,
    trigger: endpoint?.name || endpoint?.endpoint_key || "-",
    sourceSystem: endpoint?.source_system || "-",
    sources: ["External API", endpoint?.entity_scope || "mixed"].filter(Boolean),
  };
};
